import { Suspense, JSX } from "react";
import IbssRouter from "./Components/Navigation/Router/IbssRouter";
import { Helmet } from "react-helmet";
import { MsalProvider } from "@azure/msal-react";
import { appContext } from "./AppContext";
import { IbssComponent } from "./Components/Core/BaseComponent/IbssComponent";
import { LogOutReason } from "@iconics/core";
import { RouteHelper } from "./Common/RouteHelper";
import Labels from "./Data.Labels/Labels";
import ApiMessages from "./Data.Labels/ApiMessages";
import { Localisation } from "@iconics/localisation";
import Translation from "@monza/toolkit/common/localization/translation";
import AssetTranslation from "@monza/asset-controls/localization/translation";
import { ComponentsProvider } from "@iconics/components";
import { SerendieSymbol } from "@serendie/symbols";
import UnitedKingdomIcon from "./Components/Icons/Flags/UnitedKingdom";
import JapanIcon from "./Components/Icons/Flags/Japan";
import { Core } from "@iconics/core";
import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "./Providers.Msal/MsalConfig";

class App extends IbssComponent<IProps, IState>
{
    private appContext = appContext();
    private get local() { return appContext().localStorageProvider; }
    private resizeHandlerId = '';
    private readonly visibilityChangeHandler = (): void =>
    {
        void this.checkSessionOnVisibilityChange();
    };

    constructor(props: IProps)
    {
        super(props);
        this.state = {
            ready: false,
        };
    }

    public async componentDidMount(): Promise<void>
    {
        this.appContext.useDefaultSettingsProvider();
        await this.appContext.initaliseSettings();

        // initialise localisation
        appContext().localisation = new Localisation();
        appContext().localisation.useDateTimeExtensions().useNumberExtensions();

        const languageOptions = await appContext().localisation
            .createLanguageOptions()
            .useLanguages(
                {
                    code: appContext().localisation.terms,
                    text: () => appContext().labels.Platform_LanguageMenu_Terms,
                    icon: <SerendieSymbol name='type' />,
                },
                {
                    code: 'en-GB',
                    text: () => appContext().labels.Platform_LanguageMenu_English,
                    icon: <UnitedKingdomIcon />,
                },
                {
                    code: 'ja',
                    text: () => appContext().labels.Platform_LanguageMenu_Japanese,
                    icon: <JapanIcon />,
                },
            )
            .setDefaultLanguage('en-GB')
            .useDefaultLanguageProvider()
            .build();

        const labelsBuilder = await appContext().localisation
            .createLabelBuilder(new Labels())
            .setLangugeOptions(languageOptions)
            .addLabelsProvider(i => import(`./Data.Labels/${i}/Labels.json`))
            .addLabelsProvider(i => import(`./Data.Labels/en-GB/Labels.json`))
            .usePoEditorLabelsParser()
            .autoMap()
            .build();

        const apiMessagesBuilder = await appContext().localisation
            .createLabelBuilder(new ApiMessages())
            .setLangugeOptions(languageOptions)
            .addLabelsProvider(i => import(`./Data.Labels/${i}/ApiMessages.json`))
            .addLabelsProvider(i => import(`./Data.Labels/en-GB/ApiMessages.json`))
            .usePoEditorLabelsParser()
            .autoMap()
            .build();

        appContext().language = languageOptions;
        appContext().labels = labelsBuilder.labels;
        appContext().apiMessages = apiMessagesBuilder.labels;
        Translation.currentLanguage = languageOptions.current;
        AssetTranslation.currentLanguage = languageOptions.current;

        // initialise core
        appContext().core = new Core();

        const routeHelper = new RouteHelper();

        appContext().core
            .configureValidation({
                invalidFieldLabel: labelsBuilder.labels.Platform_Invalid_Field,
                isRequiredLabel: labelsBuilder.labels.Platform_Common_MandatoryField,
                mustBeNumberLabel: labelsBuilder.labels.Platform_Common_Property_MustBeNumber,
                mustBeEqualToLabel: labelsBuilder.labels.Platform_Common_MustBeEqualTo,
                mustBeGreaterThanLabel: labelsBuilder.labels.Platform_Common_Property_OutOfRangeMin,
                mustBeMaxLengthLabel: labelsBuilder.labels.Platform_Common_MustBeMaxLength,
                mustBeValidEmailLabel: labelsBuilder.labels.Platform_Common_Property_InvalidEmail,
            })
            .configureLocale(() => appContext().language.current)
            .configureApiClient({
                appVersionHeader: `FADX/${appContext().settings.internalVersion}`,
                isPublicRoute: () => routeHelper.getCurrentRoute()?.isPublic ?? false,
                isLoggingIn: () =>
                {
                    const route = routeHelper.getCurrentRoute();
                    return route?.path == "/auth" || route?.path == "/login/success";
                },
                onApiError: error => appContext().state.set({
                    showApiError: true,
                    apiErrorTitle: error.errorTitle,
                    apiErrorDetails: error.errorDetails,
                    apiErrorMessages: error.messages,
                    apiErrorStatus: error.status,
                    apiErrorInstance: error.instance,
                    apiErrorTime: error.time,
                }),
                labels: {
                    networkFailure: labelsBuilder.labels.HubLabelnetworkFailure,
                    pleaseTryAgain: labelsBuilder.labels.HubLabelPleaseTryAgain,
                    somethingWentWrong: labelsBuilder.labels.Platform_Common_SomethingWentWrong,
                },
                getApiMessage: key => (appContext().apiMessages as unknown as Record<string, string>)[key] ?? "",
            });

        // initialise components
        const labels = appContext().labels;

        appContext().components
            .initialise({
                platformApiV2Url: appContext().settings.platformApi,
                dataEntryApiV1Url: appContext().settings.dataEntryApi,
                isDevMode: appContext().settings.devMode,
            })
            .configureDeleteButton({
                buttonLabel: labels.Platform_CommonAction_Delete,
                dialogTitle: labels.Platform_ConfirmDelete_Title,
                dialogMessage: labels.Platform_ConfirmDelete_Message,
                dialogConfirmDeleteLabel: labels.Platform_CommonAction_Delete,
            })
            .configureAlertDialog({
                okButtonLabel: labels.Platform_CommonAction_Ok,
            })
            .configureApiErrorDialog({
                dialogTitle: labels.Platform_Common_Attention,
                okButtonLabel: labels.Platform_CommonAction_Ok,
            })
            .configureConfirmDialog({
                okButtonLabel: labels.Platform_CommonAction_Ok,
                cancelButtonLabel: labels.Platform_CommonAction_Cancel,
            })
            .configureLanguageDialog({
                title: labels.Platform_LanguageMenu_Title,
                subTitle: labels.Platform_LanguageMenu_SubTitle,
                languageInputLabel: labels.Platform_LanguageMenu_Language,
                okButtonLabel: labels.Platform_CommonAction_Save,
                cancelButtonLabel: labels.Platform_CommonAction_Cancel,
            })
            .configureFileDropZone({
                prompt: labels.Platform_AddFile_DropZone_Message,
                selectButtonLabel: labels.Platform_AddFile_SelectFile,
                resetButtonLabel: labels.FileDropzone_Reset,
            })
            .configureMediaSelector({
                selectButtonLabel: labels.Platform_AddSelectImage,
                dialogTitle: labels.Platform_ImageSelection,
                duplicateImageError: labels.Platform_ImageNameExistsError,
                selectedImageLabel: labels.Platform_SelectedImage,
                okButtonLabel: labels.Platform_CommonAction_Ok,
                cancelButtonLabel: labels.Platform_CommonAction_Cancel,
            })
            .configureMultilingualTextField({
                requiredLabel: labels.Platform_Required,
                unknownLanguageLabel: labels.Platform_LanguageMenu_Unknown,
                dialogTitle: labels.Platform_MultilingualTextField_Title,
                dialogSubTitle: labels.Platform_MultilingualTextField_SubTitle,
                okButtonLabel: labels.Platform_CommonAction_Save,
                cancelButtonLabel: labels.Platform_CommonAction_Cancel,
            })
            .configureTextArea({
                requiredLabel: labels.Platform_Required,
            })
            .configureTextField({
                requiredLabel: labels.Platform_Required,
            })
            .configurePasswordField({
                requiredLabel: labels.Platform_Required,
            })
            .configureSelect({
                requiredLabel: labels.Platform_Required,
            })
            .configureUserPicker({

                label: labels.HubLabelNameOrEmailLabel,
                noUserSelectedLabel: labels.funcNoUserSelected_S,
            })
            .configureTreeDataGrid({
                noDataLabel: labels.Platform_Common_NoData,
            })
            .configureTickSymbol({
                enabledAriaLabel: labels.Platform_Enabled,
                disabledAriaLabel: labels.Platform_Disabled,
            })
            .addResizeHandler(
                size => this.appContext.state.set({ screenSize: size }),
                id => this.resizeHandlerId = id
            );

        // register helpers
        this.appContext.useDefaultCache();
        this.appContext.cache.startGarbageCollection();

        // register providers
        this.appContext.useDefaultLocalStorageProvider();
        this.appContext.useDefaultSessionStorageProvider();
        this.appContext.useDefaultApiClient();
        this.appContext.useDefaultDataEntryApiClientV1();
        this.appContext.useDefaultFadxPlatformApiClientV2();
        this.appContext.useDefaultMsalProvider();
        this.appContext.useDefaultAuth0Provider();
        this.appContext.useDefaultStaticDataProvider(labelsBuilder.labels);

        // initialise MSAL
        const msal = appContext().msalProvider;
        await msal.initialise();
        await appContext().state.set({ msalClient: msal.msalClient });

        // initialise Auth0
        await appContext().auth0Provider.tryInitialise();

        appContext().state.autoMap(this, i => ({
            msalClient: i.msalClient,
        }));

        const expectedVersion = this.local.getHubVersion().split('.').slice(0, 2).join('.');
        const actualVersion = appContext().settings.internalVersion.split('.').slice(0, 2).join('.');

        if (expectedVersion != actualVersion)
        {
            this.local.setHubVersion(actualVersion);
            await appContext().authentication.logOut(LogOutReason.VersionChanged);
            return;
        }

        await this.setStateAsync({ ready: true });

        document.addEventListener("visibilitychange", this.visibilityChangeHandler);

        // apply persisted theme
        const themeId = appContext().localStorageProvider.getTheme() ?? 'konjo';
        document.documentElement.setAttribute("data-panda-theme", themeId);
        appContext().state.set({ themeId: themeId });
    }

    public componentWillUnmount(): void
    {
        document.removeEventListener("visibilitychange", this.visibilityChangeHandler);

        appContext().components
            .removeResizeHandler(this.resizeHandlerId)
            .dispose();
    }

    private async checkSessionOnVisibilityChange(): Promise<void>
    {
        if (document.visibilityState !== "visible")
        {
            return;
        }
        this.appContext.authentication.logOutIfTokenInvalid();
    }

    public render(): JSX.Element
    {
        let REACT_APP_VERSION = "";
        try
        {
            REACT_APP_VERSION = (process.env.REACT_APP_VERSION ? process.env.REACT_APP_VERSION : "");
        }
        catch (error)
        {
            REACT_APP_VERSION = "";
        }
        return (
            <>
                <Suspense fallback={(<div>Loading</div>)}>
                    <Helmet>
                        <meta name="version" content={REACT_APP_VERSION} />
                    </Helmet>
                    {
                        this.state.ready &&
                        <ComponentsProvider components={appContext().components} language={appContext().language}>
                            <MsalProvider instance={this.state.msalClient ?? new PublicClientApplication(msalConfig)}>
                                <IbssRouter />
                            </MsalProvider>
                        </ComponentsProvider>
                    }
                </Suspense>
            </>
        );
    }
}

export default App;
export interface IProps { }
export interface IState
{
    ready: boolean,
    msalClient?: PublicClientApplication;
}
